import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { UrlSegment } from '@angular/router';
import { RecordingAccess, SpaceRecording } from '../models/space-recording';
import { getDateString } from '../session-analytics/session_analytics_utils';
import { ISession } from '../models/session';
import { SpacesService } from './spaces.service';

export interface RecordingDetailsState {
  recording: SpaceRecording | null;
  isLoading: boolean;
  isPublic: boolean;
  error: HttpErrorResponse | Error | null;
}

@Injectable({
  providedIn: 'root',
})
export class RecordingDetailsService {
  // Recording details state management - single observable pattern
  private _recordingDetailsStateSubject$ = new BehaviorSubject<RecordingDetailsState>({
    recording: null,
    isLoading: false,
    isPublic: false,
    error: null,
  });

  // Expose observable for components to subscribe to
  public readonly recordingDetailsState$ = this._recordingDetailsStateSubject$.asObservable();

  constructor(private spacesService: SpacesService) {}

  // Route utility methods - specifically for single recording view route
  isSingleRecordingViewRoute(url: string): boolean {
    // Match /recording/:id pattern exactly (allowing query params and fragments)
    // Ensures it only matches single recording view, not future routes like /recording/:id/edit
    const recordingRoutePattern = /^\/recording\/[^/]+(?:\?.*)?(?:#.*)?$/;
    return recordingRoutePattern.test(url);
  }

  isSingleRecordingViewRouteFromSegments(segments: UrlSegment[]): boolean {
    // Must be exactly /recording/:id (2 segments only)
    return segments && segments.length === 2 && segments[0].path === 'recording';
  }

  extractRecordingIdFromUrl(url: string): string | null {
    // Extract recording ID, handling query params and fragments
    const urlMatch = url.match(/^\/recording\/([^/?#]+)/);
    return urlMatch && urlMatch[1] ? urlMatch[1] : null;
  }

  extractRecordingIdFromSegments(segments: UrlSegment[]): string | null {
    // Must be exactly /recording/:id (2 segments)
    if (!segments || segments.length !== 2) {
      return null;
    }
    if (segments[0].path === 'recording') {
      return segments[1].path;
    }
    return null;
  }

  /**
   * Loads recording details. Returns observable that emits the state.
   * @param recordingId The recording ID to load
   * @returns Observable of the recording details state
   */
  loadRecordingDetails(recordingId: string): Observable<RecordingDetailsState> {
    // Initialize loading state
    this._recordingDetailsStateSubject$.next({
      recording: null,
      isLoading: true,
      isPublic: false,
      error: null,
    });

    // Fetch recording details, update state via tap, and return subject observable
    return this.spacesService.getSingleRecording(recordingId).pipe(
      tap((recording) => {
        // Transform recording to state and update subject as side effect
        const isPublic = recording.access === RecordingAccess.PUBLIC;
        const state: RecordingDetailsState = {
          recording,
          isLoading: false,
          isPublic,
          error: null,
        };
        this._recordingDetailsStateSubject$.next(state);
      }),
      switchMap(() =>
        // Return the subject's observable to maintain consistent return type
        this._recordingDetailsStateSubject$.asObservable().pipe(first()),
      ),
      catchError((error) => {
        // Update state subject with error state
        const state: RecordingDetailsState = {
          recording: null,
          isLoading: false,
          isPublic: false,
          error,
        };
        this._recordingDetailsStateSubject$.next(state);
        // Return the subject's observable for consistency
        return this._recordingDetailsStateSubject$.asObservable().pipe(first());
      }),
    );
  }

  /**
   * Gets the current recording details state
   * @returns The current recording details state
   */
  getCachedRecordingDetails(): RecordingDetailsState | null {
    const state = this._recordingDetailsStateSubject$.value;
    // Return null only when we truly have no cached data yet.
    const cachedState = Boolean(state.recording) || Boolean(state.error);
    return cachedState ? state : null;
  }
  /**
   * Resets the recording details state
   */
  removeCachedRecording(): void {
    this._recordingDetailsStateSubject$.next({
      recording: null,
      isLoading: false,
      isPublic: false,
      error: null,
    });
  }

  /**
   * Updates the recording details state - for use by components
   * Preserves the recording if not being updated, but resets other fields to defaults
   * to prevent stale values (like isLoading: true) from persisting
   * @param updates Partial state updates
   */
  updateRecordingDetailsState(updates: Partial<RecordingDetailsState>): void {
    const currentState = this._recordingDetailsStateSubject$.value;
    const defaultState: RecordingDetailsState = {
      recording: null,
      isLoading: false,
      isPublic: false,
      error: null,
    };

    // Preserve recording if not being explicitly updated
    const preservedRecording: SpaceRecording | null =
      'recording' in updates ? updates.recording ?? null : currentState.recording ?? null;

    this._recordingDetailsStateSubject$.next({
      ...defaultState,
      recording: preservedRecording,
      ...updates,
    });
  }

  formatRecording(recording: any): SpaceRecording {
    const space = recording.spaceId as unknown as ISession;
    return {
      ...recording,
      spaceName: space.title,
      recordingId: recording._id,
      space,
      spaceId: space._id,
      formattedStartTime: getDateString(recording.startingTime),
    };
  }
}
